import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 border border-primary/30",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        primary: "bg-primary-gradient text-white hover:opacity-90 shadow-button hover:shadow-lg transition-all duration-300",
        soft: "bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800",
        teal: "bg-teal-500 text-white hover:bg-teal-600 shadow-sm hover:shadow",
        rose: "bg-rose-500 text-white hover:bg-rose-600 shadow-sm hover:shadow",
        amber: "bg-amber-500 text-white hover:bg-amber-600 shadow-sm hover:shadow",
        glass: "bg-white/20 dark:bg-warmGray-900/20 backdrop-blur-sm border border-white/20 dark:border-white/10 text-foreground hover:bg-white/30 dark:hover:bg-warmGray-800/30",
        aztec: "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:opacity-90 border border-white/10",
        egypt: "bg-gradient-to-r from-amber-500 to-amber-600 text-foreground hover:opacity-90 border border-black/10",
        flamingo: "bg-gradient-to-r from-rose-500 to-rose-600 text-white hover:opacity-90 border border-white/10",
        cosmic: "bg-primary-gradient text-white hover:opacity-90 shadow-button hover:shadow-lg transition-all duration-300",
        aurora: "bg-secondary-gradient text-foreground hover:opacity-90 border border-black/10",
        supernova: "bg-accent-gradient text-white hover:opacity-90 border border-white/10",
        meteor: "bg-gradient-to-r from-rose-500 to-amber-500 text-white hover:opacity-90 border border-white/10",
        orbit: "bg-gradient-to-r from-amber-400 to-teal-400 text-foreground hover:opacity-90 border border-black/10",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
